/**
 * Plans the 03 drawing's floor-plan sheets from a recognition result: which part of the
 * building each sheet shows, where the matchlines between them run, and what the keyplan
 * hatches. Deterministic geometry over the recognised walls and room tags; the connector
 * draws the result (ConnectorCommands.Build03Drawing).
 */

import type { CellPoint, Mask } from "./grid.ts";
import { close, dilate, loopArea, outerLoops } from "./grid.ts";
import { clipSheet, findJunctions } from "./clip.ts";
import { diagonalCells } from "./trace.ts";
import { findFootprint, rasterFor, toCell, toModel } from "./footprint.ts";
import type { Raster } from "./footprint.ts";
import { readRecognition } from "./input.ts";
import type { Group } from "./partition.ts";
import { partition } from "./partition.ts";
import { findRooms } from "./rooms.ts";
import type { PlanPoint, PlannedMatchline, PlannedSheet, PlannerLimits, SheetPlan } from "./types.ts";
import { DEFAULT_LIMITS } from "./types.ts";

export * from "./types.ts";

const CELL_INCHES = 6;
const CLOSE_RADIUS_INCHES = 60;
const MIN_BUILDING_SQUARE_INCHES = 600 * 144;
const SEAL_RADIUS_INCHES = 24;
const MAX_ROOM_SQUARE_INCHES = 8000 * 144;
const LEAKED_ROOM_RADIUS_INCHES = 120;
/** Kept clear around a sheet's content inside the viewport; more than the overlap, so it fits. */
const SHEET_MARGIN_INCHES = 30;
/**
 * How far a sheet's viewport reaches past its own area: across a cut, so the cut wall shows on
 * both sheets, and past the outside walls, for door swings.
 */
const OVERLAP_INCHES = 24;
/** Long enough for an "AV201" label at LABEL_TEXT_HEIGHT_INCHES to sit over the extension. */
const MATCHLINE_EXTENSION_INCHES = 84;
const LABEL_TEXT_HEIGHT_INCHES = 18;
/** A matchline may run at 45 degrees only within this distance of a wall at that angle... */
const DIAGONAL_REACH_INCHES = 72;
/** ...and only a wall at least this long, so a door leaf or a short return does not count. */
const DIAGONAL_MIN_WALL_INCHES = 48;

function areaLetter(index: number): string {
  let n = index;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function loopsInModel(raster: Raster, loops: CellPoint[][], minCells: number): PlanPoint[][] {
  return loops.filter((loop) => loopArea(loop) >= minCells).map((loop) => loop.map((p) => toModel(raster, p)));
}

function labelPoint(raster: Raster, region: Mask): PlanPoint {
  const { width } = raster.shape;
  let sumX = 0;
  let sumY = 0;
  let count = 0;
  for (let i = 0; i < region.length; i++) {
    if (region[i] !== 1) continue;
    sumX += i % width;
    sumY += Math.floor(i / width);
    count++;
  }
  const cx = sumX / count;
  const cy = sumY / count;
  let best = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < region.length; i++) {
    if (region[i] !== 1) continue;
    const d = (i % width - cx) ** 2 + (Math.floor(i / width) - cy) ** 2;
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  }
  return toModel(raster, { x: (best % width) + 0.5, y: Math.floor(best / width) + 0.5 });
}

export function planSheets(
  recognition: unknown,
  inchesPerPreviewPixel: number,
  limits: PlannerLimits = DEFAULT_LIMITS,
): SheetPlan | null {
  const input = readRecognition(recognition, inchesPerPreviewPixel);
  const raster = rasterFor(input.walls, CELL_INCHES, CLOSE_RADIUS_INCHES * 2);
  if (raster === null) return null;
  const { width, height } = raster.shape;
  const warnings: string[] = [];

  const tagCells: number[] = [];
  for (const tag of input.tags) {
    const c = toCell(raster, tag.position);
    const x = Math.floor(c.x);
    const y = Math.floor(c.y);
    if (x >= 0 && y >= 0 && x < width && y < height) tagCells.push(y * width + x);
  }

  const footprint = findFootprint(raster, input.walls, tagCells, {
    closeRadiusInches: CLOSE_RADIUS_INCHES,
    minBuildingSquareInches: MIN_BUILDING_SQUARE_INCHES,
  });
  if (footprint.buildingCount === 0) return null;
  const rooms = findRooms(raster, footprint, input.doorSeals, tagCells, {
    sealRadiusInches: SEAL_RADIUS_INCHES,
    maxRoomSquareInches: MAX_ROOM_SQUARE_INCHES,
    leakedRoomRadiusInches: LEAKED_ROOM_RADIUS_INCHES,
  });

  const circulation: number[][] = Array.from({ length: footprint.buildingCount }, () => []);
  for (let i = 0; i < footprint.building.length; i++) {
    const b = footprint.building[i]!;
    if (b >= 0 && rooms.room[i] === -1) circulation[b]!.push(i);
  }
  const window = {
    width: limits.viewportWidthPaper / limits.scale,
    height: limits.viewportHeightPaper / limits.scale,
    margin: SHEET_MARGIN_INCHES,
  };
  const groups: Group[] = [];
  let oversized = 0;
  for (let b = 0; b < footprint.buildingCount; b++) {
    const result = partition(
      {
        building: b,
        rooms: rooms.rooms.filter((room) => room.building === b),
        circulation: Int32Array.from(circulation[b]!),
      },
      raster,
      window,
    );
    groups.push(...result.groups);
    oversized += result.oversized;
  }

  const labels = new Int32Array(width * height).fill(-1);
  groups.forEach((group, g) => {
    for (const room of group.rooms) for (const i of room.cells) labels[i] = g;
    for (const i of group.circulation) labels[i] = g;
  });

  const overlapRadius = Math.max(1, Math.round(OVERLAP_INCHES / raster.cell));
  const buildingOf = groups.map((group) => group.building);
  const matchlines: PlannedMatchline[] = [];
  const clipOptions = { extensionInches: MATCHLINE_EXTENSION_INCHES, minLengthInches: 36, labelOffsetInches: 5, stubInches: 36, bandInches: 48, bendInches: 72, junctionReachInches: 192 };
  const diagonals = diagonalCells(
    input.walls.map(({ a, b }) => ({ a: toCell(raster, a), b: toCell(raster, b) })),
    raster.shape,
    Math.max(1, Math.round(DIAGONAL_REACH_INCHES / raster.cell)),
    DIAGONAL_MIN_WALL_INCHES / raster.cell,
  );
  const junctions = findJunctions(raster.shape, labels, buildingOf, Math.round(48 / raster.cell), Math.round(240 / raster.cell));
  const sheets: PlannedSheet[] = groups.map((group, g) => {
    const region = new Uint8Array(labels.length);
    for (let i = 0; i < labels.length; i++) if (labels[i] === g) region[i] = 1;
    const grown = dilate(region, raster.shape, overlapRadius);
    const clipPolygons = outerLoops(grown, raster.shape)
      .filter((loop) => loopArea(loop) >= 16)
      .map((loop) => {
        const clip = clipSheet(raster, labels, buildingOf, g, grown, loop, junctions, diagonals, clipOptions);
        matchlines.push(...clip.matchlines);
        return clip.polygon;
      });
    const hatchPolygons = loopsInModel(raster, outerLoops(close(region, raster.shape, 2), raster.shape), 64);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const loop of clipPolygons) {
      for (const p of loop) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }
    }
    return {
      index: g + 1,
      areaLetter: areaLetter(g),
      building: group.building,
      viewCenter: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
      viewWidth: maxX - minX,
      viewHeight: maxY - minY,
      clipPolygons,
      hatchPolygons,
      labelPoint: labelPoint(raster, region),
    };
  });

  if (input.tags.length === 0) {
    warnings.push("No room tags were recognised, so sheet boundaries could not avoid cutting through rooms.");
  }
  if (rooms.leaked > 0) {
    warnings.push(
      `${rooms.leaked} room${rooms.leaked === 1 ? "" : "s"} had an opening the planner could not close; ` +
        "a sheet boundary may pass through part of them.",
    );
  }
  if (oversized > 0) {
    warnings.push(`${oversized} area${oversized === 1 ? " is" : "s are"} larger than one sheet and could not be divided further.`);
  }
  if (footprint.droppedPieces > 0) {
    warnings.push(
      `${footprint.droppedPieces} small detached piece${footprint.droppedPieces === 1 ? "" : "s"} of linework ` +
        "fell outside every sheet.",
    );
  }

  return {
    version: 1,
    scale: limits.scale,
    viewportWidthPaper: limits.viewportWidthPaper,
    viewportHeightPaper: limits.viewportHeightPaper,
    labelTextHeight: LABEL_TEXT_HEIGHT_INCHES,
    sheets,
    matchlines,
    warnings,
  };
}
